// Client Portal SMS Completion Engine — reporting + send-time performance dashboard.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Activity, Loader2, CheckCircle2, CornerDownLeft, Clock, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Kpi = ({ label, value, sub, tone = 'slate' }) => {
  const tones = { slate: 'text-[#15133A]', emerald: 'text-emerald-600', indigo: 'text-indigo-600', amber: 'text-amber-600' };
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5">
      <p className="text-[10px] font-black uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-black tabular-nums ${tones[tone]}`}>{value}</p>
      {sub ? <p className="text-[11px] text-slate-500">{sub}</p> : null}
    </div>
  );
};

const statusTone = (s) => s === 'delivered' ? 'bg-emerald-50 text-emerald-700' : s === 'dry_run' ? 'bg-slate-100 text-slate-500' : 'bg-rose-50 text-rose-600';

export default function SmsAutomationReport({ open, onClose, headers }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/api/onboarding/sms-automation/report`, { headers }); setData(data); }
    catch { toast.error('Could not load SMS report'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;

  const s = data?.summary || {};
  const slots = data?.by_slot || [];
  const maxRate = Math.max(1, ...slots.map((r) => r.completion_rate));

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4" data-testid="sms-report-panel" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full sm:max-w-3xl bg-[#F6F7FB] rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white shrink-0">
          <span className="flex items-center gap-2 font-black text-[#15133A]"><Activity className="w-4 h-4 text-[#15133A]" /> Portal SMS automation · performance</span>
          <button type="button" onClick={onClose} data-testid="sms-report-close" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-300" /></div> : !data ? null : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <Kpi label="Messages sent" value={s.sent || 0} sub={s.dry_run ? `${s.dry_run} dry-run` : 'live'} />
                <Kpi label="Returned to portal" value={`${s.return_rate || 0}%`} sub={`${s.returned || 0} of ${s.sent || 0}`} tone="indigo" />
                <Kpi label="Completed after" value={`${s.completion_rate || 0}%`} sub={`${s.completed || 0} portals`} tone="emerald" />
                <Kpi label="Avg time to finish" value={s.avg_minutes_to_complete != null ? (s.avg_minutes_to_complete >= 60 ? `${Math.round(s.avg_minutes_to_complete / 60)}h` : `${s.avg_minutes_to_complete}m`) : '—'} sub={s.best_slot ? `Best slot: ${s.best_slot}` : ''} tone="amber" />
              </div>

              <div className="rounded-2xl border border-slate-200/80 bg-white p-4">
                <p className="flex items-center gap-1.5 mb-3 text-xs font-black uppercase tracking-wide text-slate-500"><TrendingUp className="w-3.5 h-3.5" /> Send-time performance (client-local)</p>
                <div className="space-y-2">
                  {slots.map((r) => (
                    <div key={r.slot} className="flex items-center gap-3" data-testid={`sms-slot-${r.slot}`}>
                      <span className="w-14 shrink-0 text-sm font-black tabular-nums text-[#15133A]">{r.slot}</span>
                      <div className="flex-1 h-6 rounded-lg bg-slate-100 overflow-hidden relative">
                        <div className={`h-full rounded-lg transition-[width] duration-700 ${r.slot === s.best_slot && r.sent ? 'bg-emerald-400' : 'bg-indigo-300'}`} style={{ width: `${r.sent ? (r.completion_rate / maxRate) * 100 : 0}%` }} />
                        <span className="absolute inset-y-0 left-2 flex items-center text-[11px] font-bold text-slate-600">{r.sent ? `${r.completion_rate}% completed` : 'no sends yet'}</span>
                      </div>
                      <span className="w-28 shrink-0 text-right text-[11px] text-slate-500 tabular-nums">{r.sent} sent · {r.returned} back</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-slate-400">As data builds, this reveals which minute in the 8:30–10:00 window converts best — the basis for future AI send-time optimisation.</p>
              </div>

              <div>
                <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">Recent sends</p>
                {!data.recent?.length ? <p className="text-sm text-slate-400 py-6 text-center">No automated messages sent yet. They'll appear here the moment the engine sends one.</p> : (
                  <ul className="space-y-2">
                    {data.recent.map((r) => (
                      <li key={r.id} className="rounded-2xl border border-slate-200/80 bg-white p-3.5" data-testid={`sms-log-${r.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-black text-[#15133A]">{r.client_name || r.reference || 'Client'}</span>
                              <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-black text-indigo-700">{r.template_name || r.rule}</span>
                              <span className="text-[11px] font-bold text-slate-400 tabular-nums">{r.slot} · {r.completion_pct ?? 0}%</span>
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${statusTone(r.status)}`}>{r.status === 'dry_run' ? 'dry-run' : r.status}</span>
                            </div>
                            <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px]">
                              {r.resumed ? <span className="inline-flex items-center gap-1 text-indigo-600 font-bold"><CornerDownLeft className="w-3 h-3" /> returned in {r.returned_minutes}m</span> : <span className="text-slate-400">no return yet</span>}
                              {r.completed_after ? <span className="inline-flex items-center gap-1 text-emerald-600 font-bold"><CheckCircle2 className="w-3 h-3" /> completed {r.minutes_to_complete != null ? `in ${r.minutes_to_complete >= 60 ? Math.round(r.minutes_to_complete / 60) + 'h' : r.minutes_to_complete + 'm'}` : ''}</span> : null}
                              <span className="inline-flex items-center gap-1 text-slate-400"><Clock className="w-3 h-3" /> {r.client_tz}</span>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
